// src/components/dashboard/BusinessHealthSection.jsx
import React, { useEffect, useState, useRef } from "react";
import { getApiClient } from "../../libs/axiosInstance";
import { getCurrencySymbol } from "../../libs/countryData";


const axios = getApiClient();

export default function BusinessHealthSection() {
  const [metrics, setMetrics] = useState(null);
  const [aiSummary, setAiSummary] = useState("");
  const [aiRecommendation, setAiRecommendation] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get("/api/stats/business-health");
        if (!res.data?.ok) return;

        setMetrics(res.data.metrics || null);
        setAiSummary(res.data.aiSummary || "");
        setAiRecommendation(res.data.aiRecommendation || "");
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  function fmtCurrency(v) {
    return `${getCurrencySymbol()}${Number(v || 0).toLocaleString("en-PH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }

  function fmtPct(v) {
    const n = Number(v || 0);
    const sign = n > 0 ? "+" : "";
    return `${sign}${n.toFixed(1)}%`;
  }

  if (loading) {
    return (
      <div className="card mb-3">
        <div className="card-body py-3 text-muted small">Loading business health…</div>
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="card mb-3">
        <div className="card-body py-3 text-muted small">
          No data yet. Start recording sales to see your business health.
        </div>
      </div>
    );
  }

  const {
    thisWeekSales,
    lastWeekSales,
    weekGrowthPct,
    thisMonthSales,
    lastMonthSales,
    monthGrowthPct,
    totalOrders30d,
    bestSellerName,
    bestSellerQty,
    businessScore,
  } = metrics;

  // microcopy
  const monthGrowthMicro =
    monthGrowthPct >= 0
      ? `Your business grew ${fmtPct(monthGrowthPct)} this month!`
      : `Your business is down ${fmtPct(monthGrowthPct)} this month.`;

  return (
    <div className="row">
      {/* Business Health Snapshot */}
      <div className="col-lg-6 mb-3">
        <div className="card h-100">
          <div className="card-header py-2 d-flex justify-content-between align-items-center">
            <h6 className="m-0">Business Health Snapshot</h6>
            <span className="badge bg-light text-muted small">Dashboard</span>
          </div>
          <div className="card-body">
            <div className="row g-3">
              <div className="col-6">
                <div className="small text-muted text-uppercase">This week</div>
                <div className="fw-bold">{fmtCurrency(thisWeekSales)}</div>
                <div
                  className="small"
                  style={{ color: weekGrowthPct >= 0 ? "#198754" : "#dc3545" }}
                >
                  vs last week: {fmtPct(weekGrowthPct)}
                </div>
              </div>
              <div className="col-6">
                <div className="small text-muted text-uppercase">This month</div>
                <div className="fw-bold">{fmtCurrency(thisMonthSales)}</div>
                <div
                  className="small"
                  style={{ color: monthGrowthPct >= 0 ? "#198754" : "#dc3545" }}
                >
                  vs last month: {fmtPct(monthGrowthPct)}
                </div>
              </div>
              <div className="col-6">
                <div className="small text-muted text-uppercase">
                  Orders (last 30 days)
                </div>
                <div className="fw-bold">{totalOrders30d}</div>
              </div>
              <div className="col-6">
                <div className="small text-muted text-uppercase">
                  Best-selling item
                </div>
                <div className="fw-bold">
                  {bestSellerName || "No sales yet"}
                </div>
                {bestSellerName && (
                  <div className="small text-muted">{bestSellerQty} sold</div>
                )}
              </div>
            </div>

            {/* Simple business score gauge */}
            <div className="mt-3">
              <div className="d-flex justify-content-between small text-muted">
                <span>Business score</span>
                <span>{businessScore}/100</span>
              </div>
              <div
                style={{
                  background: "#eee",
                  borderRadius: "999px",
                  overflow: "hidden",
                  height: "10px",
                }}
              >
                <div
                  style={{
                    width: `${businessScore}%`,
                    height: "10px",
                    transition: "width 0.4s",
                    background:
                      businessScore >= 75
                        ? "linear-gradient(90deg,#28a745,#20c997)"
                        : businessScore >= 50
                        ? "linear-gradient(90deg,#ffc107,#fd7e14)"
                        : "linear-gradient(90deg,#dc3545,#fd7e14)",
                  }}
                ></div>
              </div>
              <div className="small mt-1 text-muted">{monthGrowthMicro}</div>
            </div>
          </div>
        </div>
      </div>

      {/* AI Insights */}
      <div className="col-lg-6 mb-3">
        <div className="card h-100">
          <div className="card-header py-2 d-flex justify-content-between align-items-center">
            <h6 className="m-0">AI Insights</h6>
          </div>
          <div className="card-body">
            <div
              className="small mb-2"
              style={{ fontWeight: 500, lineHeight: 1.4 }}
            >
              {aiSummary}
            </div>
            <div className="small text-muted" style={{ lineHeight: 1.4 }}>
              {aiRecommendation}
            </div>
            <hr />
            <div className="small text-muted">
              {/* extra microcopy spot */}
              {monthGrowthPct >= 0
                ? "Great job—keep your bestsellers stocked and consider gentle upsells on slow days."
                : "Consider reviewing pricing, promos, or menu items to recover this month's dip."}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
